/**
 * Recruiter Gaze model v2, content-aware scan-path simulation.
 *
 * Grounded in recruiter eye-tracking research (≈7-second first pass):
 *   - F-pattern reading with top-third dominance (base attention field)
 *   - FIXATIONS on structural landmarks: the name block, section headings and
 *     the first lines under them, the MOST RECENT role (title/company/dates and
 *     its first bullets), and dates/tenure checks
 *   - steep drop-off on later pages
 *
 * v1 painted the base field alone, a generic gradient blind to the document.
 * v2 reads the page's actual layout (font sizes, section headings, the first
 * experience entry) and adds fixation bumps where a recruiter's eyes really
 * land, so the hot zones follow THIS resume, not just page geometry.
 * Rules-based and deterministic, no ML, instant and explainable.
 */

const SECTION_RE = /^(professional\s+summary|summary|profile|skills|core\s+competencies|technical\s+skills|experience|professional\s+experience|work\s+experience|employment|education|certifications?|licenses?)\b/i;
const YEAR_RE = /(19|20)\d{2}/;

const norm = s => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

// ── Base attention field (F-pattern + top-third dominance) ───────────────────
function baseWeight(xn, yn) {
  let v;
  if (yn <= 0.33) v = 1 - 0.25 * (yn / 0.33);                 // 1.00 → 0.75
  else if (yn <= 0.66) v = 0.75 - 0.35 * ((yn - 0.33) / 0.33); // 0.75 → 0.40
  else v = 0.4 - 0.2 * ((yn - 0.66) / 0.34);                   // 0.40 → 0.20

  const inBar = (yn >= 0.02 && yn <= 0.15) || (yn >= 0.25 && yn <= 0.38);
  let h;
  if (inBar) h = xn <= 0.7 ? 1 : 1 - 0.45 * ((xn - 0.7) / 0.3);
  else if (xn <= 0.18) h = 1;
  else h = 1 - 0.7 * Math.min(1, (xn - 0.18) / 0.55);

  return Math.max(0, Math.min(1, v * h));
}

// ── Line grouping (shared by landmark detection and keyword mapping) ─────────
export function groupLines(tokens) {
  const sorted = [...tokens].filter(t => norm(t.str)).sort((a, b) => a.y - b.y || a.x - b.x);
  const lines = [];
  for (const t of sorted) {
    const line = lines.length ? lines[lines.length - 1] : null;
    if (line && Math.abs(t.y - line.y) < Math.max(4, t.h * 0.6)) {
      line.tokens.push(t);
    } else {
      lines.push({ y: t.y, tokens: [t] });
    }
  }
  for (const line of lines) {
    line.tokens.sort((a, b) => a.x - b.x);
    line.text = line.tokens.map(t => t.str).join(' ');
    line.x = line.tokens[0].x;
    line.h = Math.max(...line.tokens.map(t => t.h));
    line.endX = Math.max(...line.tokens.map(t => t.x + (t.w || 0)));
  }
  return lines;
}

/**
 * Build the attention model for one page.
 * tokens: [{str,x,y,w,h}] in CSS px. Returns { weightAt(x,y), landmarks }.
 * Landmark fixations are gaussian-ish bumps added onto the base field.
 */
export function buildAttentionModel({ width, height, pageIndex = 0, tokens = [] }) {
  const lines = groupLines(tokens);
  const bumps = [];   // {x, y, rx, ry, amt}
  const landmarks = [];
  const bump = (x, y, rx, ry, amt, label) => {
    bumps.push({ x, y, rx, ry, amt });
    if (label) landmarks.push({ label, x, y });
  };

  const bodyH = lines.length
    ? [...lines.map(l => l.h)].sort((a, b) => a - b)[Math.floor(lines.length / 2)]
    : 12;

  if (pageIndex === 0 && lines.length) {
    // 1. Name block, the largest text in the top fifth of page 1.
    const topLines = lines.filter(l => l.y < height * 0.22);
    if (topLines.length) {
      const name = topLines.reduce((a, b) => (b.h > a.h ? b : a));
      bump(width / 2, name.y + name.h / 2, width * 0.45, name.h * 3, 0.35, 'name');
    }
  }

  // 2. Section headings + the first lines beneath each (recruiters sample the
  //    top of every section). 3. The most recent role gets the strongest bump.
  let expHeaderSeen = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isBig = line.h >= bodyH * 1.15;
    const txt = norm(line.text);
    const isSection = SECTION_RE.test(txt) && txt.length < 40;
    if (!isSection && !(isBig && txt.length < 40 && line.y > height * 0.1)) continue;
    if (!isSection) continue;

    // Heading itself + fading attention over the next ~2 lines. Only lines
    // that are spatially adjacent count, recruiters fixate under the heading,
    // not on whatever happens to come next in reading order.
    const near = (a, b, gap) => b.y - a.y > 0 && b.y - a.y < gap;
    bump(line.x + 80, line.y + line.h / 2, 140, line.h * 2.5, 0.22, `section:${txt.split(/\s/)[0]}`);
    const below = lines.slice(i + 1, i + 3).filter(b => near(line, b, line.h * 6));
    for (let j = 0; j < below.length; j++) {
      const b = below[j];
      bump((b.x + b.endX) / 2, b.y + b.h / 2, (b.endX - b.x) / 2 + 30, b.h * 1.6, 0.16 - j * 0.06);
    }

    // Most recent role: the first entry after the experience heading, title,
    // company, dates and the first ~3 bullets draw real fixation time.
    if (!expHeaderSeen && /experience|employment/.test(txt)) {
      expHeaderSeen = true;
      const roleLines = lines.slice(i + 1, i + 5).filter(r => near(line, r, line.h * 12));
      for (let j = 0; j < roleLines.length; j++) {
        const r = roleLines[j];
        bump((r.x + r.endX) / 2, r.y + r.h / 2, (r.endX - r.x) / 2 + 30, r.h * 1.6,
             0.3 - j * 0.05, j === 0 ? 'most-recent-role' : null);
      }
    }
  }

  // 4. Dates, recruiters fixate on tenure/gaps.
  for (const line of lines) {
    for (const t of line.tokens) {
      if (YEAR_RE.test(t.str)) {
        bump(t.x + (t.w || 30) / 2, t.y + t.h / 2, (t.w || 30) / 2 + 16, t.h * 1.4, 0.12);
      }
    }
  }

  const pageFactor = pageIndex === 0 ? 1 : 0.35 / pageIndex;

  const weightAt = (x, y) => {
    let w = baseWeight(x / width, y / height);
    for (const b of bumps) {
      const dx = (x - b.x) / b.rx;
      const dy = (y - b.y) / b.ry;
      const d2 = dx * dx + dy * dy;
      if (d2 < 4) w += b.amt * Math.exp(-d2);
    }
    return Math.max(0, Math.min(1, w)) * pageFactor;
  };

  return { weightAt, landmarks, lines };
}

// ── Zones ────────────────────────────────────────────────────────────────────
export const ZONES = {
  hot:  { min: 0.60, label: 'Seen first',     color: '#ea580c' },
  warm: { min: 0.35, label: 'Skimmed',        color: '#d97706' },
  cold: { min: 0,    label: 'Likely skipped', color: '#2563eb' },
};

export function classifyWeight(w) {
  if (w >= ZONES.hot.min) return 'hot';
  if (w >= ZONES.warm.min) return 'warm';
  return 'cold';
}

export function heatColor(w) {
  if (w >= ZONES.hot.min) return `rgba(255, 96, 0, ${0.08 + 0.24 * w})`;
  if (w >= ZONES.warm.min) return `rgba(255, 180, 40, ${0.06 + 0.16 * w})`;
  return `rgba(70, 130, 255, ${0.05 + 0.06 * (1 - w)})`;
}

/**
 * Map keywords onto the page using the attention model.
 * Sub-token positioning uses the hit token's real width (not a glyph guess).
 * Returns one entry per keyword: {keyword, found, x, y, w, h, weight, zone}.
 */
export function mapKeywords(tokens, keywords, weightAt) {
  const lines = groupLines(tokens);

  return keywords.map(keyword => {
    const kw = norm(keyword);
    if (!kw) return { keyword, found: false };

    let best = null;
    for (const line of lines) {
      const lineText = norm(line.text);
      const idx = lineText.indexOf(kw);
      if (idx === -1) continue;

      // Walk tokens to the one containing the match start, tracking real x.
      let acc = 0;
      let hit = line.tokens[0];
      let accBefore = 0;
      for (const t of line.tokens) {
        const len = norm(t.str).length + 1; // +1 join space
        if (idx < acc + len) { hit = t; accBefore = acc; break; }
        acc += len;
      }
      const hitLen = Math.max(1, norm(hit.str).length);
      const frac = Math.min(1, Math.max(0, (idx - accBefore) / hitLen));
      const x = hit.x + frac * (hit.w || hitLen * hit.h * 0.5);
      const w = Math.min(
        (kw.length / hitLen) * (hit.w || hitLen * hit.h * 0.5) * 1.05,
        line.endX - x
      );
      const weight = weightAt(x + w / 2, line.y + line.h / 2);
      if (!best || weight > best.weight) {
        best = { keyword, found: true, x, y: line.y, w: Math.max(w, 20), h: line.h, weight, zone: classifyWeight(weight) };
      }
    }
    return best || { keyword, found: false };
  });
}

export function summarizePlacements(placements) {
  return {
    hot: placements.filter(p => p.found && p.zone === 'hot').map(p => p.keyword),
    warm: placements.filter(p => p.found && p.zone === 'warm').map(p => p.keyword),
    cold: placements.filter(p => p.found && p.zone === 'cold').map(p => p.keyword),
    notFound: placements.filter(p => !p.found).map(p => p.keyword),
  };
}

/**
 * Collision-free pin layout: numbered pins instead of overlapping text labels.
 * Returns placements (found only) with {pinX, pinY, index} added, pins nudge
 * upward until they stop overlapping a previously placed pin.
 */
export function layoutPins(placements) {
  const found = placements.filter(p => p.found);
  const placed = [];
  const R = 9; // pin radius
  const overlaps = (x, y) =>
    placed.some(q => Math.abs(q.px - x) < R * 2.2 && Math.abs(q.py - y) < R * 2.2);
  found.forEach((p, i) => {
    let px = p.x + p.w / 2;
    let py = p.y - R - 2;
    let guard = 0;
    while (guard < 12 && overlaps(px, py)) {
      py -= R * 2.2;
      guard += 1;
    }
    if (py < R) py = p.y + p.h + R + 2; // fell off the top, drop below the line
    p.pinX = px;
    p.pinY = py;
    p.index = i + 1;
    placed.push({ px, py });
  });
  return found;
}
